import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Loader2, Building2, IndianRupee, Save, Briefcase, Mail, Phone, User, MessageSquare, CheckCircle2, Clock, XCircle, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { VO_TIERS, VO_TIER_MAP } from '../../lib/virtualOfficeTiers';
import VirtualOfficeSubscriptionsTab from './VirtualOfficeSubscriptionsTab';
import SellVirtualOfficeModal from './SellVirtualOfficeModal';

const STATUS_COLORS = {
  new: { bg: 'bg-blue-500/10', text: 'text-blue-600', label: 'New', icon: Clock },
  contacted: { bg: 'bg-amber-500/10', text: 'text-amber-600', label: 'Contacted', icon: Clock },
  converted: { bg: 'bg-green-500/10', text: 'text-green-600', label: 'Converted', icon: CheckCircle2 },
  lost: { bg: 'bg-red-500/10', text: 'text-red-600', label: 'Lost', icon: XCircle },
};

const PlanRow = ({ tier, plan, venueId, onSave, saving, cardBg, inputBg }) => {
  const [price, setPrice] = useState(plan?.monthly_price ?? '');
  const [active, setActive] = useState(plan?.is_active ?? false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setPrice(plan?.monthly_price ?? '');
    setActive(plan?.is_active ?? false);
    setDirty(false);
  }, [plan?.monthly_price, plan?.is_active]);

  const handleSave = () => {
    const p = parseFloat(price);
    if (isNaN(p) || p < 0) { toast.error('Enter a valid price'); return; }
    onSave({ venue_id: venueId, tier: tier.id, monthly_price: p, is_active: active });
    setDirty(false);
  };

  return (
    <div className={`p-3 rounded-lg ${cardBg} border flex flex-wrap items-center gap-3`} data-testid={`vo-plan-row-${venueId}-${tier.id}`}>
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{tier.name}</span>
          {tier.badge && <Badge variant="outline" className="text-[10px] py-0">{tier.badge}</Badge>}
        </div>
        <p className="text-xs text-muted-foreground">{tier.tagline}</p>
      </div>
      <div className="flex items-center gap-1.5">
        <IndianRupee className="w-3 h-3 text-muted-foreground" />
        <Input
          type="number"
          value={price}
          onChange={(e) => { setPrice(e.target.value); setDirty(true); }}
          placeholder="Monthly price"
          className={`${inputBg} w-32 h-9`}
          data-testid={`vo-price-${venueId}-${tier.id}`}
        />
        <span className="text-xs text-muted-foreground">/month</span>
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={active} onCheckedChange={(v) => { setActive(v); setDirty(true); }} data-testid={`vo-toggle-${venueId}-${tier.id}`} />
        <span className="text-xs text-muted-foreground">{active ? 'Active' : 'Disabled'}</span>
      </div>
      <Button size="sm" onClick={handleSave} disabled={!dirty || saving} data-testid={`vo-save-${venueId}-${tier.id}`}>
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
        <span className="ml-1.5">Save</span>
      </Button>
    </div>
  );
};

const PlansManager = ({ api, theme }) => {
  const [venues, setVenues] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, pRes] = await Promise.all([
        api.get('/virtual-office/managed-venues'),
        api.get('/virtual-office/plans'),
      ]);
      setVenues(vRes.data || []);
      setPlans(pRes.data || []);
    } catch (e) {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      await api.post('/virtual-office/plans', payload);
      toast.success('Saved');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const planFor = (venueId, tier) => plans.find((p) => p.venue_id === venueId && p.tier === tier);

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      {venues.length === 0 && <Card className={theme.cardBg}><CardContent className="p-8 text-center text-muted-foreground">No venues in your scope.</CardContent></Card>}
      {venues.map((venue) => {
        const venuePlans = plans.filter((p) => p.venue_id === venue.id);
        const activeCount = venuePlans.filter((p) => p.is_active).length;
        return (
          <Card key={venue.id} className={theme.cardBg} data-testid={`vo-venue-card-${venue.id}`}>
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{venue.name}</h3>
                  <p className="text-xs text-muted-foreground">{venue.region || venue.address}</p>
                </div>
                <Badge variant={activeCount > 0 ? 'default' : 'outline'}>
                  {activeCount} / {VO_TIERS.length} active
                </Badge>
              </div>
              <div className="space-y-2">
                {VO_TIERS.map((tier) => (
                  <PlanRow
                    key={tier.id}
                    tier={tier}
                    plan={planFor(venue.id, tier.id)}
                    venueId={venue.id}
                    onSave={handleSave}
                    saving={saving}
                    cardBg={theme.innerCardBg || theme.cardBg}
                    inputBg={theme.inputBg}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

const EnquiriesManager = ({ api, theme }) => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/virtual-office/enquiries${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`);
      setEnquiries(res.data || []);
    } catch (e) {
      toast.error('Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/virtual-office/enquiries/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (e) {
      toast.error('Update failed');
    }
  };

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap">
        {['all', 'new', 'contacted', 'converted', 'lost'].map((s) => (
          <Button
            key={s}
            size="sm"
            variant={statusFilter === s ? 'default' : 'outline'}
            onClick={() => setStatusFilter(s)}
            data-testid={`vo-enquiry-filter-${s}`}
          >
            {s === 'all' ? 'All' : STATUS_COLORS[s]?.label || s}
          </Button>
        ))}
        <div className="text-sm text-muted-foreground self-center ml-2">{enquiries.length} enquiries</div>
      </div>

      {enquiries.length === 0 && (
        <Card className={theme.cardBg}><CardContent className="p-12 text-center text-muted-foreground">
          <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-40" />
          <p>No enquiries yet.</p>
        </CardContent></Card>
      )}

      {enquiries.map((e) => {
        const st = STATUS_COLORS[e.status] || STATUS_COLORS.new;
        const Icon = st.icon;
        return (
          <Card key={e.id} className={theme.cardBg} data-testid={`vo-enquiry-${e.id}`}>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold">{e.company_name}</h4>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${st.bg} ${st.text} flex items-center gap-1`}>
                      <Icon className="w-3 h-3" /> {st.label}
                    </span>
                    {e.tier_name && <Badge variant="outline" className="text-xs">{e.tier_name}</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{e.venue_name} · {new Date(e.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {e.status === 'new' && <Button size="sm" variant="outline" onClick={() => updateStatus(e.id, 'contacted')} data-testid={`vo-mark-contacted-${e.id}`}>Mark Contacted</Button>}
                  {e.status !== 'converted' && e.status !== 'lost' && <Button size="sm" onClick={() => updateStatus(e.id, 'converted')} data-testid={`vo-mark-converted-${e.id}`}>Won</Button>}
                  {e.status !== 'lost' && e.status !== 'converted' && <Button size="sm" variant="outline" onClick={() => updateStatus(e.id, 'lost')} data-testid={`vo-mark-lost-${e.id}`}>Lost</Button>}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                <div className="flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-muted-foreground" /> {e.contact_name}</div>
                <a href={`mailto:${e.email}`} className="flex items-center gap-1.5 hover:text-primary"><Mail className="w-3.5 h-3.5 text-muted-foreground" /> {e.email}</a>
                <a href={`tel:${e.phone}`} className="flex items-center gap-1.5 hover:text-primary"><Phone className="w-3.5 h-3.5 text-muted-foreground" /> {e.phone}</a>
              </div>
              {e.message && <p className="text-sm mt-2 p-2 rounded bg-muted/30">{e.message}</p>}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

const VirtualOfficeTab = ({ api, theme = {}, isAdmin = false }) => {
  const [sellOpen, setSellOpen] = useState(false);
  return (
    <div className="space-y-6" data-testid="virtual-office-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-primary" />
            Virtual Office
          </h2>
          <p className="text-sm text-muted-foreground mt-1">Manage virtual office plans per centre, sell subscriptions, and follow up on enquiries.</p>
        </div>
        <Button onClick={() => setSellOpen(true)} data-testid="vo-open-sell-modal">
          <Briefcase className="w-4 h-4 mr-2" /> Sell to Client
        </Button>
      </div>

      <Tabs defaultValue="plans" className="w-full">
        <TabsList>
          <TabsTrigger value="plans" data-testid="vo-tab-plans">Plans by Centre</TabsTrigger>
          <TabsTrigger value="subscriptions" data-testid="vo-tab-subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="enquiries" data-testid="vo-tab-enquiries">Enquiries</TabsTrigger>
        </TabsList>
        <TabsContent value="plans" className="mt-4">
          <PlansManager api={api} theme={theme} />
        </TabsContent>
        <TabsContent value="subscriptions" className="mt-4">
          <VirtualOfficeSubscriptionsTab api={api} theme={theme} isAdmin={isAdmin} />
        </TabsContent>
        <TabsContent value="enquiries" className="mt-4">
          <EnquiriesManager api={api} theme={theme} />
        </TabsContent>
      </Tabs>

      <SellVirtualOfficeModal open={sellOpen} onClose={() => setSellOpen(false)} api={api} />
    </div>
  );
};

export default VirtualOfficeTab;
